import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Sheet,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/joy';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';
import { useTheme } from '@mui/joy/styles';

import MainContent from '../common/MainContent';
import PageHero from '../common/PageHero';
import {
  fetchAdminUserSpiritualProfile,
  updateAdminUserSpiritualProfile,
  type SpiritualProfile,
} from '../../api';
import { getSevaGradientConfig, getSevaGradientSurface } from '../../theme/branding';

type FormState = {
  initiated: boolean;
  sikshaGuruName: string;
  dikshaGuruName: string;
  ashram: string;
  mentor: string;
  servicePreference: string;
};

type FeedbackState = { type: 'success' | 'danger'; message: string } | null;

const EMPTY_FORM: FormState = {
  initiated: false,
  sikshaGuruName: '',
  dikshaGuruName: '',
  ashram: '',
  mentor: '',
  servicePreference: '',
};

const toFormState = (profile?: SpiritualProfile | null): FormState => ({
  initiated: Boolean(profile?.initiated),
  sikshaGuruName: profile?.sikshaGuruName ?? '',
  dikshaGuruName: profile?.dikshaGuruName ?? '',
  ashram: profile?.ashram ?? '',
  mentor: profile?.mentor ?? '',
  servicePreference: profile?.servicePreference ?? '',
});

const toPayload = (form: FormState) => ({
  initiated: form.initiated,
  sikshaGuruName: form.sikshaGuruName.trim() || undefined,
  dikshaGuruName: form.dikshaGuruName.trim() || undefined,
  ashram: form.ashram.trim() || undefined,
  mentor: form.mentor.trim() || undefined,
  servicePreference: form.servicePreference.trim() || undefined,
});

export default function AdminSpiritualProfile({ userId }: { userId: number | string }) {
  const theme = useTheme();
  const gradientSurface = getSevaGradientSurface(theme, { withShadow: true });
  const gradientTokens = getSevaGradientConfig(theme.palette.mode);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [hasExistingProfile, setHasExistingProfile] = useState(false);

  const initialFormRef = useRef<FormState>({ ...EMPTY_FORM });
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setFromProfile = useCallback((profile: SpiritualProfile | null) => {
    const normalized = toFormState(profile);
    initialFormRef.current = { ...normalized };
    setForm(normalized);
    setHasExistingProfile(Boolean(profile));
  }, []);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFeedback(null);

    try {
      const data = await fetchAdminUserSpiritualProfile(userId);
      if (!isMountedRef.current) return;
      setFromProfile(data ?? null);
    } catch (err) {
      if (!isMountedRef.current) return;
      setError('We were unable to load this devotee\'s spiritual journey. Please refresh in a few moments.');
      setFromProfile(null);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [setFromProfile, userId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const hasChanges = useMemo(() => {
    const initial = initialFormRef.current;
    return (
      form.initiated !== initial.initiated ||
      form.sikshaGuruName !== initial.sikshaGuruName ||
      form.dikshaGuruName !== initial.dikshaGuruName ||
      form.ashram !== initial.ashram ||
      form.mentor !== initial.mentor ||
      form.servicePreference !== initial.servicePreference
    );
  }, [form]);

  const handleReset = useCallback(() => {
    setForm({ ...initialFormRef.current });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setFeedback(null);

    try {
      await updateAdminUserSpiritualProfile(userId, toPayload(form));
      if (!isMountedRef.current) return;

      initialFormRef.current = { ...form };
      setHasExistingProfile(true);
      setFeedback({
        type: 'success',
        message: 'Spiritual journey details have been saved. Thank you for nurturing this devotee\'s seva path.',
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      setFeedback({
        type: 'danger',
        message: 'We could not save the updated spiritual profile. Please try again.',
      });
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  }, [form, userId]);

  const disabledActions = loading || saving;

  return (
    <MainContent>
      <PageHero
        eyebrow="Devotional stewardship"
        title="Spiritual profile"
        description="Document initiations, mentors, and service aspirations so guidance remains heartfelt and personal."
        actions={[
          <Button
            key="refresh"
            size="md"
            variant="outlined"
            color="neutral"
            startDecorator={<RefreshRoundedIcon />}
            onClick={loadProfile}
            disabled={disabledActions}
          >
            Refresh profile
          </Button>,
          <Button
            key="reset"
            size="md"
            variant="plain"
            color="neutral"
            startDecorator={<RestartAltRoundedIcon />}
            onClick={handleReset}
            disabled={disabledActions || !hasChanges}
          >
            Reset changes
          </Button>,
          <Button
            key="save"
            size="md"
            variant="solid"
            color="primary"
            startDecorator={<VolunteerActivismRoundedIcon />}
            loading={saving}
            onClick={handleSave}
            disabled={!hasChanges || loading}
            data-testid="save-spiritual-profile"
          >
            Save spiritual journey
          </Button>,
        ]}
      />

      {error ? (
        <Alert
          color="danger"
          variant="soft"
          sx={{ alignItems: 'flex-start', borderRadius: 'lg' }}
          startDecorator={<ErrorOutlineRoundedIcon />}
          endDecorator={
            <Button size="sm" variant="soft" color="danger" onClick={loadProfile} disabled={loading}>
              Try again
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Card
          variant="outlined"
          sx={{ borderRadius: 'xl', p: { xs: 2.5, md: 3 }, gap: 2 }}
          data-testid="spiritual-profile-loading"
        >
          <Stack spacing={2}>
            <Skeleton variant="text" level="h2" width="45%" />
            <Skeleton variant="text" level="body-md" width="60%" />
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 'md' }} />
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 'md' }} />
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 'md' }} />
          </Stack>
        </Card>
      ) : (
        <Grid container spacing={3} alignItems="stretch">
          <Grid xs={12} md={7} lg={8}>
            <Card variant="outlined" sx={{ borderRadius: 'xl', p: { xs: 2.5, md: 3 }, height: '100%' }}>
              <Stack spacing={2.5} sx={{ height: '100%' }}>
                <Stack spacing={0.5}>
                  <Typography level="title-lg" fontWeight={700}>
                    Seva journey details
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary', maxWidth: 520 }}>
                    Capture the devotee\'s devotional guidance so invitations, mentorship, and seva pairings remain aligned wit
h their aspirations.
                  </Typography>
                </Stack>

                <Sheet
                  variant="soft"
                  color="primary"
                  sx={{
                    borderRadius: 'lg',
                    px: 2,
                    py: 1.25,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FavoriteRoundedIcon fontSize="small" />
                  <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.88)' }}>
                    {form.initiated
                      ? 'Initiated devotee — celebrate their diksha journey with reverence.'
                      : 'Not initiated yet — guide them lovingly through siksha and seva opportunities.'}
                  </Typography>
                </Sheet>

                <Stack spacing={2}>
                  <FormControl orientation="horizontal" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                    <FormLabel sx={{ fontWeight: 600 }}>Initiated</FormLabel>
                    <Checkbox
                      checked={form.initiated}
                      onChange={(event) =>
                        setForm((previous) => ({ ...previous, initiated: event.target.checked }))
                      }
                      size="md"
                      color="primary"
                      slotProps={{ input: { 'aria-label': 'initiated status' } }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel id="siksha-guru-label" htmlFor="siksha-guru">
                      Siksha guru
                    </FormLabel>
                    <Input
                      value={form.sikshaGuruName}
                      onChange={(event) =>
                        setForm((previous) => ({ ...previous, sikshaGuruName: event.target.value }))
                      }
                      placeholder="e.g. HH Lokanath Swami Maharaj"
                      slotProps={{ input: { id: 'siksha-guru', 'aria-labelledby': 'siksha-guru-label' } }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel id="diksha-guru-label" htmlFor="diksha-guru">
                      Diksha guru
                    </FormLabel>
                    <Input
                      value={form.dikshaGuruName}
                      onChange={(event) =>
                        setForm((previous) => ({ ...previous, dikshaGuruName: event.target.value }))
                      }
                      placeholder="e.g. HH Gopal Krishna Goswami Maharaj"
                      slotProps={{ input: { id: 'diksha-guru', 'aria-labelledby': 'diksha-guru-label' } }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel id="ashram-label" htmlFor="ashram">
                      Ashram
                    </FormLabel>
                    <Input
                      value={form.ashram}
                      onChange={(event) =>
                        setForm((previous) => ({ ...previous, ashram: event.target.value }))
                      }
                      placeholder="e.g. Brahmachari, Grihastha"
                      slotProps={{ input: { id: 'ashram', 'aria-labelledby': 'ashram-label' } }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel id="mentor-label" htmlFor="mentor">
                      Mentor
                    </FormLabel>
                    <Input
                      value={form.mentor}
                      onChange={(event) => setForm((previous) => ({ ...previous, mentor: event.target.value }))}
                      placeholder="Primary mentor or counselor"
                      slotProps={{ input: { id: 'mentor', 'aria-labelledby': 'mentor-label' } }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel id="service-preference-label" htmlFor="service-preference">
                      Service preference
                    </FormLabel>
                    <Input
                      value={form.servicePreference}
                      onChange={(event) =>
                        setForm((previous) => ({ ...previous, servicePreference: event.target.value }))
                      }
                      placeholder="e.g. Kirtan, book distribution, deity worship"
                      slotProps={{ input: { id: 'service-preference', 'aria-labelledby': 'service-preference-label' } }}
                    />
                  </FormControl>
                </Stack>

                {saving ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.tertiary' }}>
                    <CircularProgress size="sm" />
                    <Typography level="body-sm">Saving updates…</Typography>
                  </Stack>
                ) : null}
              </Stack>
            </Card>
          </Grid>

          <Grid xs={12} md={5} lg={4}>
            <Stack spacing={2.5} sx={{ height: '100%' }}>
              <Card
                variant="soft"
                color="primary"
                sx={{
                  ...gradientSurface,
                  color: gradientSurface.color,
                  borderRadius: 'xl',
                  p: { xs: 2.5, md: 3 },
                  gap: 1.5,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(80% 90% at 15% 10%, rgba(255,255,255,0.25) 0%, transparent 65%), radial-gradient(75% 75% at 85% 85%, rgba(255,210,130,0.22) 0%, transparent 70%)',
                    pointerEvents: 'none',
                  }}
                />
                <Stack spacing={1} sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography level="title-md" fontWeight={700}>
                    Honour their devotional path
                  </Typography>
                  <Typography level="body-sm" sx={{ color: gradientTokens.muted, maxWidth: 320 }}>
                    These insights help match seva, counseling, and announcements with the devotee\'s current spiritual guidan
ce and aspirations.
                  </Typography>
                </Stack>
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 'xl', p: { xs: 2.25, md: 2.75 }, gap: 1.5 }}>
                <Stack spacing={1}>
                  <Typography level="title-sm" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.tertiary' }}>
                    Stewardship notes
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    {hasExistingProfile
                      ? 'Review and refresh these details whenever the devotee progresses in their journey to keep guidance attuned.'
                      : 'Begin their profile so mentors and seva leads can shepherd them with warmth and clarity.'}
                  </Typography>
                </Stack>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={4000}
        variant="soft"
        color={feedback?.type === 'danger' ? 'danger' : 'success'}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setFeedback(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          color={feedback?.type === 'danger' ? 'danger' : 'success'}
          variant="soft"
          startDecorator={feedback?.type === 'danger' ? <ErrorOutlineRoundedIcon /> : <CheckCircleRoundedIcon />}
        >
          {feedback?.message}
        </Alert>
      </Snackbar>
    </MainContent>
  );
}

